<script setup lang="ts">
import { useAllowedFileTypesGetAllowedFileTypes as useGetAllowedFileTypes } from '@services/documenten-public/orval-api/allowed-file-types/allowed-file-types';
import FileDropOverlay from '@shared/component/FileDropOverlay/FileDropOverlay.vue';
import VProgressBar from '@shared/component/VProgressBar/VProgressBar.vue';
import UploadField from '@shared/form/UploadField/UploadField.vue';
import { computed, ref, useTemplateRef } from 'vue';
import ClassicDialog from '@shared/form/ClassicDialog/ClassicDialog.vue';
import BestandenUploadenDialoog, {
  type FileForm,
} from '@shared/form/BestandenUploadenDialoog/BestandenUploadenDialoog.vue';
import {
  bestandenBestandToevoegen,
  getBestandenBestandenOphalenQueryKey,
} from '@services/communicatie-public/orval-api/bestanden-api/bestanden-api';
import { VButton } from '@shared/component/VButton';
import { useQueryClient } from '@tanstack/vue-query';
import { type SUREbusinessCommonDomainEntityReferencesSbEntityType } from '@services/communicatie-public/orval-api/model/sUREbusinessCommonDomainEntityReferencesSbEntityType';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { maatwerkdossierDocumentUploaden } from '@services/aanvragen-public/orval-api/maatwerkdossier-api/maatwerkdossier-api';
import { useErrorHandler } from '@errors/useErrorHandler';
import VModal from '@shared/component/VModal/VModal.vue';
import { SUREbusinessCommonDomainDocumentSoort as DocumentSoort } from '@services/documenten-public/orval-api/model/sUREbusinessCommonDomainDocumentSoort';

const { entityType, entityId } = defineProps<{
  entityType: SUREbusinessCommonDomainEntityReferencesSbEntityType;
  entityId?: string;
}>();

const emit = defineEmits<{ documentUploaded: [string]; uploadFinished: [] }>();

const { handleError } = useErrorHandler();
const queryClient = useQueryClient();

const selectedFiles = ref<File[]>([]);
const uploadenDialoogOpen = ref(false);
const isUploadingFiles = ref(false);
const uploadProgress = ref(0);
const uploadFieldRef = useTemplateRef('uploadField');

const { data: allowedFileTypes, isPending: fileTypesLoading } = useGetAllowedFileTypes(undefined, {
  query: { select: ({ result }) => result },
});

const beschikbareDocumentsoorten = computed(() => {
  const map: Partial<Record<string, DocumentSoort[]>> = {
    Maatwerk: [DocumentSoort.MaatwerkDocument],
    Aanvraag: [DocumentSoort.Aanvraag],
    KantoorDocument: [DocumentSoort.KantoorDocument],
  };

  return map[entityType];
});

function onFilesSelected(files: File[]) {
  if (allowedFileTypes.value === undefined || !files.length) return;

  selectedFiles.value = files;
  uploadenDialoogOpen.value = true;
}

function uploadFiles(fileForms: FileForm[]) {
  if (isUploadingFiles.value) return;

  uploadenDialoogOpen.value = false;
  setTimeout(async () => {
    isUploadingFiles.value = true;
    let hasError = false;

    const promises = fileForms.map((file) => {
      const body = {
        File: file.file,
        'Dto.Bestandsnaam': file.naam,
        ...(file.soort ? { 'Dto.DocumentType': file.soort } : {}),
        ...(file.toelichting ? { 'Dto.Toelichting': file.toelichting } : {}),
        ...(file.geheim ? { 'Dto.IsGeheim': true } : {}),
      };

      return entityType === 'Maatwerk'
        ? maatwerkdossierDocumentUploaden(Number(entityId), {
            ...body,
            ...(file.afzender ? { 'Dto.AfkomstigePartij': file.afzender } : {}),
          })
        : bestandenBestandToevoegen(entityType, entityId, body)
            .catch((error) => {
              hasError = true;
              handleError(error);
            })
            .then((documentGuid) => {
              if (typeof documentGuid === 'string') {
                emit('documentUploaded', documentGuid);
              }
            })
            .finally(() => {
              uploadProgress.value++;
            });
    });

    await Promise.allSettled(promises);
    emit('uploadFinished');

    if (entityType === 'Maatwerk') {
      setTimeout(
        () => {
          globalThis.location.reload();
        },
        hasError ? 10000 : 0,
      );
    }

    if (!hasError) {
      await queryClient.invalidateQueries({
        queryKey: getBestandenBestandenOphalenQueryKey(entityType, entityId),
      });
    }

    isUploadingFiles.value = false;
  }, 500);
}
</script>

<template>
  <div>
    <VButton
      v-if="entityType !== 'Maatwerk'"
      @click="() => uploadFieldRef?.activate()"
      :loading="fileTypesLoading"
      font-size="sm"
      inverted
      prepend-icon="far fa-file-upload"
    >
      <span class="!font-normal !text-grey-700">Drag & drop of</span> kies bestanden
    </VButton>
    <!-- file upload button -->
    <!--waarom handmatige values? omdat ik hier een classic button emuleer die niet aan onze moderne tailwind standaarden voldoet.-->
    <label
      v-else
      for="file-upload"
      class="!mb-0 !mt-0.5 !cursor-pointer !rounded-[3px] !border !border-[#1ab394] !bg-[#1ab394] !px-5 !py-[6px] !font-['Corbel'] !text-[14px] !leading-[21px] !text-white !transition-colors !ease-in-out hover:!border-[#18a689] hover:!bg-[#18a689]"
    >
      <VSkeleton v-if="fileTypesLoading" />
      <FontAwesomeIcon v-else class="mr-[10px]" :icon="['far', 'paperclip']" />Bestand(en) uploaden
    </label>

    <UploadField
      v-if="!fileTypesLoading"
      :allowed-file-types
      id="file-upload"
      class="hidden"
      :max-size="30 * 1024 * 1024"
      multiple
      @update:value="onFilesSelected"
      ref="uploadField"
      reset-input-value
    />

    <BestandenUploadenDialoog
      :classic-dialog="entityType === 'Maatwerk'"
      :is-active="uploadenDialoogOpen"
      :files="selectedFiles"
      :selectable-document-types="beschikbareDocumentsoorten"
      :toon-afkomstige-partij-veld="entityType === 'Maatwerk'"
      @close="() => (uploadenDialoogOpen = false)"
      @upload-files="uploadFiles"
    />
    <component
      :is="entityType === 'Maatwerk' ? ClassicDialog : VModal"
      :title="`${selectedFiles.length === 1 ? 'Bestand' : `${selectedFiles.length} bestanden`} uploaden`"
      :is-active="isUploadingFiles"
      :hide-close-button="entityType === 'Maatwerk'"
    >
      <template v-if="selectedFiles.length === 1">Bestand uploaden...</template>
      <template v-else-if="uploadProgress < selectedFiles.length">
        Bestanden uploaden... {{ uploadProgress + 1 }} van {{ selectedFiles.length }}
      </template>
      <template v-else> Uploaden gelukt! pagina bijwerken... </template>
      <VProgressBar
        :progress="
          selectedFiles.length === 1
            ? 50
            : (100 / (selectedFiles.length + 1)) * (uploadProgress + 1)
        "
        shade="primary"
      />
      <template #footer><!-- leeg omdat VModal anders knoppen toont --></template>
    </component>

    <FileDropOverlay @files-dropped="onFilesSelected" v-if="!fileTypesLoading" />
  </div>
</template>
